import React, { useEffect, useState } from 'react';
import AncServiceModal from '../AncServiceModal';
import { useNavigate, useParams } from 'react-router-dom';
import { getData, getDataWithCache } from '../../../../../services/CommonApiService';
import endpoints from '@config/endpoints';
import { useDispatch, useSelector } from 'react-redux';
import { setAncNumber, setdiffInWeeks, setFacilityDetails, setLmpDate, setPregnancyId, updateAncService, updateDeliveryDetails, updatePNC } from '@store/actions/beneficiaryAction';
import { RootState } from '@store/reducers';
import FormatDate from '@utils/dateFormat';
import dayjs from 'dayjs';
import { DELIVERY_COMMON_CONSTANTS, DELIVERY_DATE_FORMATS } from '../../DeliveryOutcome/constants/DeliveryConstants';
import { errorLogger } from '@utils/applicationLogUtils';
import { ancPeriodDateCalculation } from '../Forms/CreateAndEdit/AncDateCaluculation';
import { getOngoingPNCDetails, PNC_COMMON_CONSTANTS } from '../../PNC/PNCConstants';
import { areDatesSame, diffBetweenTwoDays } from '@utils/commonUtils';
import { ANC_SUB_SERVICE } from '@constants/ancConstants';
import { benef_const } from '@constants/beneficiaryConstant';
import { getServiceDetailsUnderCurrentDate } from '@services/ancApiServices';
import { getDeliveryOutcomeByPregnancyId } from '@services/pregnancyApiServices';
import { ANC_SERVICE_NAME } from '../Forms/CreateAndEdit/ancConstants';

interface AncServiceProps {
  showModal: boolean;
  handleModalClose: () => void;
  modalContent: any;
}

const AncServices: React.FC<AncServiceProps> = ({
  showModal,
  handleModalClose,
  modalContent,
}) => {
  const errorObj = {
    showError: false,
    errorMessage: '',
    errorField: '',
    isDisable: false
  }
  const navigate = useNavigate();
  const [dynamicModalContent, setDynamicModaContent] = useState<any>(modalContent);
  const [ancServiceItems, setAncServiceItems] = useState<any>();
  const [modalError, setModalError] = useState<any>([errorObj]);
  const [deliveryCreateDate, setDeliveryCreateDate] = useState<any>();
  const [deliveryDetails, setDeliveryDetails] = useState<any>();
  const [serviceListData, setServiceListData] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const dispatch = useDispatch();

  const pregnancyId = useSelector((state: RootState) => state.beneficiary.currPregnancyId);
  const moduleCode = useSelector((state: RootState) => state.beneficiary.moduleCode);
  const pregHistoryDetails = useSelector((state: RootState) => state.beneficiary.pregHistoryDetails);
  const isAutoPregnancyClosure = moduleCode === PNC_COMMON_CONSTANTS.AUTO_CLOSURE_PREGNANCY;
  const pregnancyDetails = useSelector((state: RootState) => state.beneficiary.viewPregnancyDetails);
  const includeNormalFacilityDetailsPage = benef_const.abortionCare;
  const includeFacilityDetailsPage = benef_const.ancServices;
  const includeDeliveryFacilityDetailsPage = benef_const.deliveryServices;
  const includePNCFacilityDetailsPage = benef_const.pncServices;
  const serviceList = useSelector((state: RootState) => state.beneficiary.pregnancyServices);
  const currLmpDate = useSelector((state: RootState) => state.beneficiary.currLmpDate);
  const viewPregnancyDetails = useSelector((state: RootState) => state.beneficiary.viewPregnancyDetails);
  const beneficiaryDetails = useSelector((state: RootState) => state.beneficiary.beneficiaryDetails);
  const onSameDay = useSelector((state: RootState) => state.beneficiary.onSameDay);
  const benefPregnancyDetails = useSelector(
    (state: RootState) => state.beneficiary.pregnancyDetails,
  );
  useEffect(() => {
    setDynamicModaContent(modalContent);
  }, [modalContent]);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      if (!pregnancyId) {
        if (!cancelled) setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        await fetchDeliveryDetail();
        const anyPEOHServiceTaken = serviceList?.some((item: any) => item?.is_pe_oh_service_taken);
        const anyRoutineAncTaken = serviceList?.some((item: any) => item?.is_routine_anc_taken);
        let latestPeObstetricServiceData;
        if(anyPEOHServiceTaken && pregnancyId) {
        const latestServiceRes = await getData<any>(`${endpoints.getLatestServiceAvailedDateByServiceName}/${pregnancyId}/${ANC_SERVICE_NAME.PE_OBSTETRIC}`);
        latestPeObstetricServiceData = latestServiceRes?.data?.[0];
        }
        handleAncSubModalError(anyPEOHServiceTaken, anyRoutineAncTaken,latestPeObstetricServiceData);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    init();
    return () => { cancelled = true; };
  }, [pregnancyId]);
  
  const getDateBasedOnValidation = (date: any, noOfDaysToAdd: number, unit: any,) => {
    return dayjs(date).add(noOfDaysToAdd, unit);
  };

  const isPNCAllowed = (placeOfDeliveryCode:any, deliveryDate:any, dateOfDischarge: any) => {
    const daysDiff = dayjs().diff(dayjs(deliveryDate), 'day');
    if ((placeOfDeliveryCode === PNC_COMMON_CONSTANTS.TRANSIT || placeOfDeliveryCode === PNC_COMMON_CONSTANTS.INSTITUTIONAL) && daysDiff < 2) {
      return false;
    }
    if(dayjs(dateOfDischarge).isAfter(dayjs(), 'day')) {
      return false;
    }

    const pncLastPeriodEnd = 44;
    const pncEndWithCoolingPeriod = pncLastPeriodEnd + 90;
    const isPNCDisabled = daysDiff > pncEndWithCoolingPeriod;

    if(isPNCDisabled) {
      return false;
    }

    return true;
  }

  const fetchDeliveryDetail = async () => {
    try {
      const formattedLmpDate = FormatDate(currLmpDate);
      const minDateBasedOnLmp = getDateBasedOnValidation(formattedLmpDate, 140, DELIVERY_COMMON_CONSTANTS.DAY);
      const deliveryResponse = await getDeliveryOutcomeByPregnancyId(pregnancyId);
      const isDeliveryServiceTaken = deliveryTaken(deliveryResponse?.data[0]?.date_of_delivery);
      if(isDeliveryServiceTaken) {
        dispatch(updateDeliveryDetails('deliveryVisitDate', deliveryResponse?.data[0]?.date_of_visit));
      }
      
      const deliveryDate = deliveryResponse?.data[0]?.date_of_delivery;
      const placeOfDeliveryCode = deliveryResponse?.data[0]?.place_of_delivery_code;
      const dateOfDischarge = deliveryResponse?.data[0]?.date_of_discharge;
      const isPncDisabled = !deliveryDate || !isPNCAllowed(placeOfDeliveryCode, deliveryDate, dateOfDischarge)
      const isDeliveryEnabled = minDateBasedOnLmp ? dayjs(minDateBasedOnLmp).isAfter(dayjs()) : false;
      handleAncModalError(isDeliveryServiceTaken, isPncDisabled, isDeliveryEnabled);
      setDeliveryCreateDate(deliveryResponse?.data[0]?.create_date);
      const deliveryDetails = {
        deliveryDate: deliveryDate,
        placeOfDeliveryCode: placeOfDeliveryCode,
        visitDate: deliveryResponse?.data[0]?.date_of_visit,
        dateOfDischarge: deliveryResponse?.data[0]?.date_of_discharge,
      }
      setDeliveryDetails(deliveryDetails);
    } catch(error:any){
      errorLogger(error?.response?.data||error?.response||error)
    }
  }


  const fetchServiceContent = async (currServiceCategoryId:any) => {
    try {
      const serviceRes = await getDataWithCache<any>(`${endpoints.getServiceSubCategory}/${currServiceCategoryId}/false`);
      setAncServiceItems(serviceRes?.data);
      const serviceContent = serviceRes?.data?.map((item: any) => item?.service_subcategory_name);
      return serviceContent;
    } catch(error:any){
      errorLogger(error?.response?.data||error?.response||error)
    }
  };
  const handleDeliveryModalError = async () => {
    try {
      if(!pregnancyId) {
        errorLogger('Pregnancy ID is not available');
        return;
      }
      const serviceTakenRes = await getData<any>(`${endpoints.deliveryPreparednessServiceCompletionList(pregnancyId)}`);
      const lmpDate = FormatDate(viewPregnancyDetails?.lmp);
      const serviceList: any = await getData(`${endpoints.getServiceListByPregnancyId}/${pregnancyId}/${lmpDate}`);
      let anc1,anc2,anc3,anc4=false;
      let isDeliveryPreparednessDone=false
      let isDeliveryPreparednessTaken=false
      serviceList?.data?.forEach((element:any) => {
          switch (element.service_period_name) {
            case 'Delivery Preparedness ':
              isDeliveryPreparednessDone=true
              isDeliveryPreparednessTaken=true
              break;
            case 'ANC 1':
              anc1 = true;
              break;
            case 'ANC 2':
              anc2 = true;
              break;
            case 'ANC 3':
              anc3 = true;
              break;
            case 'ANC 4':
              anc4 = true;
              break;
            default:
              break;
          }
        });
        if(anc1 && anc2 && anc3 && anc4){
          isDeliveryPreparednessDone=true
        }
      const requiredServices = ['hb_gm', 'bp_systolic_mm_hg', 'bp_diastolic_mm_hg'];
      const isAllServiceTaken = serviceTakenRes?.data?.every((item: any) => {
        if (requiredServices.includes(item.service_code)) {
          return item.is_service_taken;
        }
        return true;
      });

      const isBackDatedAllowed = isBackedEntryAllowed(deliveryCreateDate);
      const isPncDisabled = !deliveryDetails?.deliveryDate || !isPNCAllowed(deliveryDetails?.placeOfDeliveryCode, deliveryDetails?.deliveryDate, deliveryDetails?.dateOfDischarge)
      const isDeliveryTaken = deliveryDetails?.deliveryDate ? true : false;
      const isPNCTaken = serviceList?.data?.some((item: any) => item.service_category_code === PNC_COMMON_CONSTANTS.PNC_MOTHER); 
      setModalError((prevState: any) => [
        ...prevState,
        {
          showError: (isAutoPregnancyClosure || isPNCTaken) ? false : ((!isPncDisabled || isDeliveryTaken) ? false : !isAllServiceTaken) || !isDeliveryPreparednessTaken,
          errorMessage: 'Please fill the mandatory services on delivery preparedness form.',
          errorField: 'Delivery Details',
          isDisable: (isAllServiceTaken ? !isBackDatedAllowed : true) || !isPncDisabled || isDeliveryTaken || !isDeliveryPreparednessTaken || isAutoPregnancyClosure
        },
        {
          showError: false,
          errorMessage: '',
          errorField: 'Delivery Preparedness',
          isDisable: !isPncDisabled || isDeliveryTaken || isAutoPregnancyClosure || isDeliveryPreparednessTaken
        },
        {
          showError: false,
          errorMessage: '',
          errorField: 'PNC Mother',
          isDisable: !isAutoPregnancyClosure ? isPncDisabled : !isAutoPregnancyClosure,
        },
        {
          showError: false,
          errorMessage: '',
          errorField: 'ANC',
          isDisable: !isPncDisabled || isAutoPregnancyClosure,
        }
      ]);
    } catch(error:any){
      errorLogger(error?.response?.data||error?.response||error)
    }
  }
  const isBackedEntryAllowed = (date:any) => {
    const today = dayjs(new Date());
    const serviceCreateDate = date ? dayjs(date) : '';
    return serviceCreateDate ? today.isSame(serviceCreateDate, 'day') : true;
  }

  const deliveryTaken = (date:any) => {
    return date ? true : false;
  }

  const isANCServiceDisabled = () => {
    const fourthAncEndDate: any = ancPeriodDateCalculation(currLmpDate, 4).endDate;
    return dayjs(new Date()).isAfter(dayjs(fourthAncEndDate).add(15, 'day'));
  }

  const handleAncModalError = (isDeliveryServiceTaken:boolean, isPncDisabled:boolean, isDeliveryEnabled:boolean) => {
    setModalError((prevState: any) => [
      ...prevState,
      {
        showError: false,
        errorMessage: '',
        errorField: 'ANC',
        isDisable: !isPncDisabled || isDeliveryServiceTaken || isAutoPregnancyClosure,
      },
      {
        showError: false,
        errorMessage: '',
        errorField: 'Delivery',
        isDisable: isDeliveryEnabled,
      },
      {
        showError: false,
        errorMessage: '',
        errorField: 'Abortion Care',
        isDisable: ((pregHistoryDetails?.delivery_status !== PNC_COMMON_CONSTANTS.ON_GOING) || (diffBetweenTwoDays(currLmpDate, dayjs(), 'days') > 230))
      },
    ]);
  }

  const navigationMap: { [key: string]: { route: string; action: () => void } } = {
    'anc_pe_oh': {
      route: '/beneficiaries/anc-service/pe-obstretricHistory',
      action: () => navigate('/beneficiaries/anc-service/pe-obstretricHistory'),
    },
    'anc_rout_anc': {
      route: '/beneficiaries/anc-service/routine-anc',
      action: () => navigate('/beneficiaries/anc-service/routine-anc'),
    },
    'anc_anc_serv': {
      route: '/beneficiaries/anc-service/anc-service-form',
      action: () => navigate('/beneficiaries/anc-service/anc-service-form'),
    },
    'anc_spl_tst': {
      route: '/beneficiaries/anc-service/special-test',
      action: () => navigate('/beneficiaries/anc-service/special-test'),
    },
    'anc_td': {
      route: '/beneficiaries/anc-service/td-injection',
      action: () => navigate('/beneficiaries/anc-service/td-injection'),
    },
    'anc_usg': {
      route: '/beneficiaries/anc-service/usg',
      action: () => navigate('/beneficiaries/anc-service/usg'),
    },
    'delivery_details': {
      route: '/beneficiaries/delivery-details',
      action: () => navigate('/beneficiaries/delivery-details'),
    },
    'death_related_details':{
      route: '/beneficiaries/death/death-form', 
      action: () => navigate('/beneficiaries/death/death-form')
    },
    'abortion_care':{
      route: '/beneficiaries/abortion/abortion-form', 
      action: () => navigate('/beneficiaries/abortion/abortion-form')
    },
    'delivery_preparedness':{
      route: '/beneficiaries/delivery-preparedness', 
      action: () => navigate('/beneficiaries/delivery-preparedness')
    },
    'pnc_mother': {
      route: '/beneficiaries/pnc/create-pnc',
      action: () => navigate('/beneficiaries/pnc/create-pnc'),
    },
  };

  const handleSetContentMap = async(item: any, currServiceCategoryId: any) => {
    if (item === 'Delivery') {
      await handleDeliveryModalError();
      const content = await fetchServiceContent(currServiceCategoryId);
      const availableServices = ['Delivery Preparedness', 'Delivery Details', 'PNC Mother'];
      const filteredContent = content?.filter((item: any) => availableServices.includes(item));
      return {
        title: 'Delivery Services',
        subTitle: 'Services',
        content: filteredContent || [],
      };
    } else if (item === 'Other Services') {
      const content = await fetchServiceContent(currServiceCategoryId);
      return {
        title: 'Other Services',
        subTitle: 'Services',
        content: content || [],
      }
    } else if (item === 'ANC') {
      const content = await fetchServiceContent(currServiceCategoryId);
      return {
        title: 'ANC Services',
        subTitle: 'Services',
        content: content || [],
      };
    }
  }
  
  const handleAncSubModalError = (isPEObstericTaken:boolean, isRoutineANCTaken:boolean,latestPeObstetricServiceData?:any) => {
    setModalError((prevState: any) => [
      ...prevState,
      {
        showError: false,
        errorMessage: '',
        errorField: ANC_SUB_SERVICE.PE_OBSTETRIC_HISTORY,
        isDisable: isPEObstericTaken ? dayjs(latestPeObstetricServiceData?.latest_create_date).isSame(dayjs(),'day')?false:true:false,
      },
      {
        showError: false,
        errorMessage: '',
        errorField: ANC_SUB_SERVICE.ROUTINE_ANC,
        isDisable: !isPEObstericTaken,
      },
      {
        showError: false,
        errorMessage: '',
        errorField: ANC_SUB_SERVICE.ADDITIONAL_ANC_SERVICE,
        isDisable: !isPEObstericTaken || !isRoutineANCTaken,
      }
    ]);
  }
  const handleFacilityDetailsPage = async (item: string) => {
    const { route, action } = navigationMap[item];
    const service = ancServiceItems?.find((service: any) => service.service_subcategory_code === item);
    const serviceDetails = {
      currRoute: route,
    };
    dispatch(updateAncService('currServiceSubCtgryId', service?.service_subcategory_id));
    try {
      const ongoingPregnancyDetails =benefPregnancyDetails?.data?.length>0 ? benefPregnancyDetails :  await getData<any>(`${endpoints.getPregnancyDetailsById}/${pregnancyId}`);
      const msInWeek = 7 * 24 * 60 * 60 * 1000;
      const currentDate = new Date();
      const date2 = new Date(FormatDate(ongoingPregnancyDetails.data[0].lmp));
      const newDate = new Date(date2);
      newDate.setDate(newDate.getDate());
      const diffInMs = Math.abs(currentDate.getTime() - newDate.getTime());
      const diffInWeeks = Math.floor(diffInMs / msInWeek);
      let ancNumber = 0;
      if (diffInWeeks <= 13) {
        ancNumber = 1;
      } else if (diffInWeeks >= 14 && diffInWeeks <= 27) {
        ancNumber = 2;
      } else if (diffInWeeks >= 28 && diffInWeeks <= 35) {
        ancNumber = 3;
      } else {
        ancNumber = 4;
      }

      // Check if a service was already created today under a different ANC period to avoid mismatch
      const ancServiceListItems = serviceList?.filter((svc: any) =>
        svc.service_category_code === 'anc'
      ) ?? [];

      const todayService = ancServiceListItems.find((svc: any) =>
        svc.create_date && dayjs(svc.create_date).isSame(dayjs(), 'day')
      );

      const effectiveAncNumber = todayService
        ? parseInt(todayService.service_period_name?.replace('ANC ', '') || ancNumber)
        : ancNumber;

      dispatch(setAncNumber(effectiveAncNumber));
      dispatch(setLmpDate(FormatDate(ongoingPregnancyDetails.data[0].lmp)));
      dispatch(setdiffInWeeks(diffInWeeks));

      const sameDayCheck = await getServiceDetailsUnderCurrentDate('anc', beneficiaryDetails[0]?.benef_id, pregnancyId, effectiveAncNumber, service?.service_subcategory_id);
      let isTodayRecord = false;

      if (sameDayCheck.data.length !== 0) {
        isTodayRecord = ancServiceListItems.some((svc: any) =>
          svc.create_date &&
          dayjs(svc.create_date).isSame(dayjs(), 'day')
        );
      }

     if (isTodayRecord) {
        dispatch(updateAncService('onSameDay', true));
        dispatch(setFacilityDetails({
          facilityId: sameDayCheck.data[0].facility_id,
          healthProviderId: sameDayCheck.data[0].healthprovider_id,
          mobilizerId: sameDayCheck.data[0].mobilizer_id,
          visitTypeCode: sameDayCheck.data[0].visit_type_code,
        }));
        action();
        return;
      }else {
        navigate('/beneficiaries/service', { state: serviceDetails });
        dispatch(updateAncService('onSameDay', false));
        return;
      }
    } catch (e) {
      return;
    }
  };

  const handleDeliveryFacilityDetailsPage = async (item: string) => {
    const service = ancServiceItems?.find((service: any) => service.service_subcategory_code === item);
    const { route, action } = navigationMap[item];
    const serviceDetails = {
      currRoute: route,
      isdeliveryDetails: true,
    };
    dispatch(updateAncService('currServiceSubCtgryId', service?.service_subcategory_id));
    dispatch(setLmpDate(FormatDate(pregnancyDetails.lmp)));
    dispatch(updateAncService('onSameDay', false));
    if (!deliveryDetails?.deliveryDate) {
      navigate('/beneficiaries/service', { state: serviceDetails });
      return;
    }
    action();
    return;
  };

  const handlePNCFacilityDetailsPage = async (item: string) => {
    const { route } = navigationMap[item];
    try {
      const pncDetails = getOngoingPNCDetails(deliveryDetails?.deliveryDate, dayjs());
      let isPNCFirstVisit = false;

      if (pncDetails[0]?.pnc) {
        const pncMetaData = await getServiceDetailsUnderCurrentDate('pnc', beneficiaryDetails[0]?.benef_id, pregnancyId, pncDetails[0]?.pnc, '');
        if (pncMetaData.data.length !== 0) {
          const pncDataLength = pncMetaData?.data?.length;
          if (pncDataLength === 1 && pncMetaData?.data?.[0]?.is_pregnancy_registered_outside) {
            isPNCFirstVisit = pncMetaData?.data?.[0]?.is_pregnancy_registered_outside;
          }
        }
      }
      
      const isPncTaken = serviceListData?.data?.some((item: any) => item.service_category_code === PNC_COMMON_CONSTANTS.PNC_MOTHER);
      if (isAutoPregnancyClosure) {
        isPNCFirstVisit = !isPncTaken;
      }
      dispatch(updatePNC('pncOnSameDay', false)); // always false — detection moves to PNCFacilityDetails
      dispatch(updatePNC('isPNCFirstVisit', isPNCFirstVisit));

      //always navigate to facility details
      const serviceDetails = { currRoute: route };
      navigate('/beneficiaries/pnc/service', { state: serviceDetails });

      const service = ancServiceItems?.find((service: any) => service.service_subcategory_code === item);
      dispatch(updatePNC('currServiceSubCtgryId', service?.service_subcategory_id));
    } catch (error: any) {
      errorLogger(error?.response?.data || error?.response || error);
    }
  };

  const handleNormalFacilityDetailsPage = (item: string) => {
    const service = ancServiceItems?.find((service: any) => service.service_subcategory_code === item);
    const { route } = navigationMap[item];
    const serviceDetails = {
      currRoute: route,
    };
    dispatch(updateAncService('currServiceSubCtgryId', service?.service_subcategory_id));
    navigate('/beneficiaries/abortion/service-facility-details', { state: serviceDetails });
  };

  const handleNavigationMap = (item: string) => {
    const { action } = navigationMap[item];
    action();
  };

  const handleContentMap = async (item: string) => {
    setIsLoading(true);
    try{
      const currServiceCategoryId = dynamicModalContent?.data?.data.find((data: any) => data?.service_category_name === item)?.service_category_id;
      dispatch(updateAncService('serviceCtgryId', currServiceCategoryId));
      const dynamicContent: any = await handleSetContentMap(item, currServiceCategoryId);
      setDynamicModaContent({
        title: dynamicContent?.title,
        subTitle: dynamicContent?.subTitle,
        content: dynamicContent?.content,
      });
    }finally{
      setIsLoading(false);
    }
  };

  const getServiceDetails = (subCategoryName:any) => {
    return ancServiceItems?.find((item: any) => item?.service_subcategory_name === subCategoryName);
  }

  const handleServiceItemClick = async (item: string) => {
    const serviceCode = getServiceDetails(item);
    const serviceCategoryCode = serviceCode?.service_subcategory_code;
    if (includeFacilityDetailsPage.filter((ele: any)=> ele === serviceCategoryCode).length > 0) {
      await handleFacilityDetailsPage(serviceCategoryCode);
    } else if (includeDeliveryFacilityDetailsPage.filter((ele: any)=> ele === serviceCategoryCode).length > 0) {
      await handleDeliveryFacilityDetailsPage(serviceCategoryCode);
    } else if (includePNCFacilityDetailsPage.filter((ele: any)=> ele === serviceCategoryCode).length > 0) {
      await handlePNCFacilityDetailsPage(serviceCategoryCode);
    } 
    // else if (includeNormalFacilityDetailsPage.filter((ele: any)=> ele === item).length > 0) {
    //   handleNormalFacilityDetailsPage(item);
    // } 
    else if (Object.prototype.hasOwnProperty.call(navigationMap, serviceCategoryCode)) {
      const service = ancServiceItems?.find((service: any) => service.service_subcategory_code === serviceCategoryCode);
      dispatch(updateAncService('currServiceSubCtgryId', service?.service_subcategory_id));
      handleNavigationMap(serviceCategoryCode);
    } else if (item === 'Delivery' || item === 'Other Services' || item === 'ANC') {
      await handleContentMap(item);
    }
  };
 

  const handleBack = (item: string) => {
    if (item === 'Add Services') {
      handleModalClose();
    } else {
      setDynamicModaContent(modalContent);
    }
  }




  return (
    <div>
      <AncServiceModal
        showModal={showModal}
        onClose={handleModalClose}
        content={dynamicModalContent.content}
        modalTitle={dynamicModalContent.title}
        modalSubTitle={dynamicModalContent.subTitle}
        handleItemClick={handleServiceItemClick}
        handleBack={handleBack}
        errors={modalError}
        loading={isLoading}
      />
    </div>
  );
};

export default AncServices;